import React, { useMemo, useState } from 'react';
import { Search, X, UserRound } from 'lucide-react';

// Searchable employee selector for the monthly attendance report.
export const EmployeePicker = ({ employees, value, onChange, placeholder = 'Search employee for monthly report…' }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const selected = employees.find((e) => e.employee_id === value) || null;

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? employees.filter((e) =>
          e.full_name?.toLowerCase().includes(q) ||
          e.employee_id?.toLowerCase().includes(q) ||
          e.email?.toLowerCase().includes(q))
      : employees;
    return list.slice(0, 8);
  }, [employees, query]);

  if (selected) {
    return (
      <div
        data-testid="report-employee-selected"
        className="h-10 pl-3 pr-1.5 inline-flex items-center gap-2 rounded-lg border border-violet-300 dark:border-violet-700 bg-violet-50 dark:bg-violet-900/30 text-sm text-violet-800 dark:text-violet-200"
      >
        <UserRound className="h-4 w-4" />
        <span className="font-medium truncate max-w-[200px]">{selected.full_name}</span>
        <span className="text-xs opacity-70">{selected.employee_id}</span>
        <button
          type="button"
          data-testid="report-employee-clear"
          onClick={() => { onChange(''); setQuery(''); }}
          title="Show all employees"
          className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-violet-200/60 dark:hover:bg-violet-800/60"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative min-w-[260px]">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
      <input
        data-testid="report-employee-search"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        className="h-10 w-full pl-9 pr-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-400"
      />
      {open && (
        <ul
          data-testid="report-employee-options"
          className="absolute z-30 mt-1 w-full max-h-72 overflow-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg py-1"
        >
          {matches.length === 0 ? (
            <li className="px-3 py-2 text-sm text-slate-500">No employees match “{query}”</li>
          ) : matches.map((e) => (
            <li key={e.employee_id}>
              <button
                type="button"
                data-testid={`report-employee-option-${e.employee_id}`}
                onMouseDown={(ev) => ev.preventDefault()}
                onClick={() => { onChange(e.employee_id); setOpen(false); setQuery(''); }}
                className="w-full text-left px-3 py-2 hover:bg-violet-50 dark:hover:bg-violet-900/30"
              >
                <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{e.full_name}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">{e.employee_id} · {e.department || 'Unassigned'}</div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
